// Copyright (c) 2026 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useCancelUserServiceRequest } from '@hooks/mutations/collateral/onboarding/useCancelUserServiceRequest';
import { useRejectUserServiceRequest } from '@hooks/mutations/collateral/onboarding/useRejectUserServiceRequest';
import { useTerminateUserService } from '@hooks/mutations/collateral/onboarding/useTerminateUserService';
import { useAcceptUserServiceRequest } from '@hooks/mutations/collateral/useAcceptUserServiceRequest';
import { useCollateralUserServiceV2 } from '@hooks/queries/collateralv2/onboarding/useUserService';
import { useUserServiceRequestsV2 } from '@hooks/queries/collateralv2/onboarding/useUserServiceRequests';
import { useUserServicesV2 } from '@hooks/queries/collateralv2/onboarding/useUserServices';
import { useSnackbar } from 'notistack';
import { useCallback, useState } from 'react';
import { useErrorBoundary } from 'react-error-boundary';
import { Box, Divider, Tab, Tabs, Typography } from '@mui/material';
import { GridColDef, GridRenderCellParams, GridToolbarContainer } from '@mui/x-data-grid';
import {
  UserService,
  UserServiceRequest,
} from '@daml.js/utility-collateral-app-v1/lib/Utility/Collateral/App/Service/User';
import { ContractId } from '@daml/types';
import { AsyncButton } from '../../../components/Button/AsyncButton';
import { SyncButton } from '../../../components/Button/SyncButton';
import { RejectDialog } from '../../../components/Dialog/RejectDialog';
import Loading from '../../../components/Loading';
import { NotificationBadge } from '../../../components/NotificationBadge';
import PartyDisplay from '../../../components/PartyDisplay';
import { DataTable } from '../../../components/Table/DataTable';
import { useModuleParty } from '../../../contexts/ModuleContext';
import useOperatorConfiguration from '../../../hooks/queries/collateralv2/configuration/useOperatorConfiguration';
import useCredentials from '../../../hooks/queries/credential/useCredentials';
import { validateFulfillsAllRequirements } from '../../../utils/credential';
import { ERR_COL_ACC_PROVIDER_SVC, ERR_COL_ACC_USER_SVC } from '../../../utils/strings';
import { PageWrapper } from '../../PageWrapper';
import { RequestUserServiceDialog } from './RequestUserServiceDialog';

type Row = {
  contractId: string;
  serviceType: string;
  operator: string;
  user: string;
};

type Action = {
  name: string;
  action: (r: Row) => Promise<unknown>;
};

const createHeaders: (createActions: (r: Row) => Action[]) => GridColDef<Row>[] = (
  createActions,
) => {
  const renderActionCell = (params: GridRenderCellParams<Row>) => {
    const actions = createActions(params.row);
    return (
      <>
        {actions.map(({ name, action }) => (
          <AsyncButton key={name} sx={{ mr: 1 }} onClick={() => action(params.row)}>
            {name}
          </AsyncButton>
        ))}
      </>
    );
  };

  return [
    {
      field: 'serviceType',
      headerName: 'Service Type',
      valueGetter: (params) => params.row.serviceType,
      flex: 150,
      filterable: true,
    },
    {
      field: 'operator',
      headerName: 'Operator',
      renderCell: (params) => <PartyDisplay party={params.row.operator} />,
      valueGetter: (params) => params.row.operator,
      flex: 150,
      filterable: true,
    },
    {
      field: 'user',
      headerName: 'User',
      renderCell: (params) => <PartyDisplay party={params.row.user} />,
      flex: 150,
      valueGetter: (params) => params.row.user,
      filterable: true,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      sortable: false,
      renderCell: (params) => renderActionCell(params),
      flex: 150,
      filterable: false,
    },
  ];
};

const Toolbar = () => {
  const [userIsOpen, setUserIsOpen] = useState<boolean>(false);
  const party = useModuleParty();
  const userService = useCollateralUserServiceV2();
  const userServiceRequests = useUserServiceRequestsV2();
  const { showBoundary } = useErrorBoundary<Error>();

  const isLoading = userService.isLoading || userServiceRequests.isLoading;
  if (isLoading) return null;

  if (!userServiceRequests.data) {
    showBoundary(
      new Error('Failed to load toolbar.', {
        cause: 'User service requests are not available.',
      }),
    );
    return null;
  }
  const userServiceRequest = userServiceRequests.data.find((r) => r.payload.user === party);

  const canRequestUser = !userService.data && !userServiceRequest;

  return (
    <GridToolbarContainer>
      {!!userIsOpen && (
        <RequestUserServiceDialog open={!!userIsOpen} onClose={() => setUserIsOpen(false)} />
      )}

      <SyncButton onClick={() => setUserIsOpen(true)} disabled={!canRequestUser}>
        Request Collateral service
      </SyncButton>
    </GridToolbarContainer>
  );
};

export const Onboarding = () => {
  const credentials = useCredentials();
  const [onReject, setOnReject] = useState<((reason: string) => Promise<unknown>) | undefined>();
  const party = useModuleParty();
  const userServices = useUserServicesV2();
  const userServiceRequests = useUserServiceRequestsV2();
  const terminateUserService = useTerminateUserService();
  const acceptUserServiceRequest = useAcceptUserServiceRequest();
  const rejectUserServiceRequest = useRejectUserServiceRequest();
  const cancelUserServiceRequest = useCancelUserServiceRequest();

  const operatorConfiguration = useOperatorConfiguration();
  const { enqueueSnackbar } = useSnackbar();

  const terminateUser = useCallback(
    (r: Row) => {
      return terminateUserService({
        cid: r.contractId as ContractId<UserService>,
        argument: {
          actor: party,
        },
      });
    },
    [terminateUserService, party],
  );

  const acceptUser = useCallback(
    async (r: Row) => {
      // Check if data is loaded

      if (!credentials.data) {
        enqueueSnackbar(ERR_COL_ACC_USER_SVC, { variant: 'error' });
        return;
      }
      if (!operatorConfiguration.data) {
        enqueueSnackbar(ERR_COL_ACC_PROVIDER_SVC, { variant: 'error' });
        return;
      }
      // Find matching credentials
      const { userRequirements } = operatorConfiguration.data.payload;
      const now = new Date(); // local time
      const utcNow = new Date(now.toUTCString()); // utc time
      const { fulfillsAllRequirements, fulfillingCredentials } = validateFulfillsAllRequirements(
        utcNow,
        r.user,
        userRequirements,
        credentials.data,
      );

      if (!fulfillsAllRequirements) {
        enqueueSnackbar(`${ERR_COL_ACC_USER_SVC}: No matching credentials could be found.`, {
          variant: 'error',
        });
      }

      // Accept the request
      const payload = {
        operatorConfigurationCid: operatorConfiguration.data.contractId,
        credentialCids: fulfillingCredentials.map((c) => c.contractId),
      };

      await acceptUserServiceRequest({
        cid: r.contractId as ContractId<UserServiceRequest>,
        argument: payload,
      });
    },
    [acceptUserServiceRequest, credentials, enqueueSnackbar, operatorConfiguration.data],
  );

  const rejectUser = useCallback(
    (r: Row, reason: string) => {
      return rejectUserServiceRequest({
        cid: r.contractId as ContractId<UserServiceRequest>,
        argument: { reason },
      });
    },
    [rejectUserServiceRequest],
  );

  const cancelUser = useCallback(
    (r: Row) => {
      return cancelUserServiceRequest({
        cid: r.contractId as ContractId<UserServiceRequest>,
        argument: {},
      });
    },
    [cancelUserServiceRequest],
  );

  const createServiceActions = (r: Row) => {
    if (r.serviceType === 'UserService' && r.operator === party)
      return [{ name: 'Terminate', action: terminateUser }];
    return [];
  };

  const [value, setValue] = useState('0');
  const onChange = (_: React.SyntheticEvent, newValue: string) => {
    setValue(newValue);
  };
  const createRequestActions = (r: Row) => {
    if (r.user !== party || r.operator === party) {
      if (r.serviceType === 'UserService')
        return [
          { name: 'Accept', action: acceptUser },
          {
            name: 'Reject',
            action: () => {
              setOnReject(() => (reason: string) => rejectUser(r, reason));
              return Promise.resolve();
            },
          },
        ];
      return [];
    }
    if (r.user === party) {
      if (r.serviceType === 'UserService') return [{ name: 'Cancel', action: cancelUser }];
      return [];
    }
    return [];
  };

  const isLoading =
    userServices.isLoading ||
    userServiceRequests.isLoading ||
    !userServices.data ||
    !userServiceRequests.data;
  if (isLoading) return <Loading />;

  const serviceRows = userServices.data.map((r) => ({
    contractId: r.contractId as string,
    serviceType: 'UserService',
    operator: r.payload.operator,
    user: r.payload.user,
  }));

  const requestRows = userServiceRequests.data.map((r) => ({
    contractId: r.contractId as string,
    serviceType: 'UserService',
    operator: r.payload.operator,
    user: r.payload.user,
  }));
  return (
    <PageWrapper pageName="Onboarding">
      {!!onReject && (
        <RejectDialog
          open={!!onReject}
          onClose={() => setOnReject(undefined)}
          onReject={onReject}
        />
      )}
      <Tabs value={value} onChange={onChange}>
        <Tab
          value="0"
          label={
            <Typography variant="body2" sx={{ textTransform: 'capitalize', mr: 1 }}>
              Requests
            </Typography>
          }
        />
        <Tab
          value="1"
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', flexDirection: 'row' }}>
              <Typography variant="body2" sx={{ textTransform: 'capitalize', mr: 1 }}>
                Requests
              </Typography>
              <NotificationBadge value={requestRows.length || 0} />
            </Box>
          }
        />
      </Tabs>
      <Divider sx={{ mb: 1 }} />
      {value === '0' && (
        <DataTable
          columns={createHeaders(createServiceActions)}
          rows={serviceRows}
          rowKey={(a) => a.contractId}
          toolbar={Toolbar}
        />
      )}
      {value === '1' && (
        <DataTable
          columns={createHeaders(createRequestActions)}
          rows={requestRows}
          rowKey={(a) => a.contractId}
        />
      )}
    </PageWrapper>
  );
};
